import React, { useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { byPrefixAndName } from '@awesome.me/kit-f089350b06/icons';
import { Tooltip } from 'react-tooltip';

import SharedDropdown from '@exonet/react-shared/lib/Elements/Dropdown';

import * as S from './styled';

const Dropdown = ({ children, alternate = false, customTrigger, disabled = false }) => {
  const { t } = useTranslation();
  const anchorId = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  const defaultTrigger = useCallback(
    (onOpen) => (
      <S.Trigger onClick={disabled ? undefined : onOpen} alternate={alternate} disabled={disabled}>
        <FontAwesomeIcon icon={byPrefixAndName.far['ellipsis-vertical']} />
      </S.Trigger>
    ),
    [alternate, disabled],
  );

  if (!customTrigger) {
    const validChildren = React.Children.toArray(children).filter(React.isValidElement);
    const items = validChildren.filter((child) => child.type === SharedDropdown.Item);

    if (validChildren.length === 1 && items.length === 1) {
      const { onClick, children: label, disabled: itemDisabled, icon } = items[0].props;
      const tooltipId = `dropdown-single-${anchorId}`;

      // Show the item's own icon on the trigger, preferring the solid variant
      // (the kit lacks some solid icons, so fall back to the icon as-is).
      const triggerIcon = byPrefixAndName.fas[icon?.iconName] || icon || byPrefixAndName.fas.pencil;

      return (
        <S.Wrapper>
          <S.Trigger
            id={tooltipId}
            type="button"
            onClick={disabled ? undefined : onClick}
            alternate={alternate}
            disabled={disabled || itemDisabled}
          >
            <FontAwesomeIcon icon={triggerIcon} />
          </S.Trigger>
          <Tooltip
            anchorSelect={`#${tooltipId}`}
            content={typeof label === 'string' ? label : undefined}
            positionStrategy="fixed"
            style={{ zIndex: 300 }}
          >
            {typeof label === 'string' ? null : label}
          </Tooltip>
        </S.Wrapper>
      );
    }
  }

  return (
    <SharedDropdown
      alternate={alternate}
      customTrigger={customTrigger || defaultTrigger}
      // A custom trigger brings its own tooltip.
      tooltip={customTrigger ? '' : t('core:common.actions')}
    >
      {children}
    </SharedDropdown>
  );
};

Dropdown.propTypes = {
  children: PropTypes.node,
  alternate: PropTypes.bool,
  customTrigger: PropTypes.func,
  disabled: PropTypes.bool,
};

Dropdown.Item = SharedDropdown.Item;
Dropdown.Separator = SharedDropdown.Separator;

export default Dropdown;
